import { useId } from "react";
import type { StanKatalogu } from "../../hooks/useKatalogParametrow.js";
import { opisKosztuWyboru, przelaczParametr } from "../../lib/parametryOceny.js";
import { IconOstrzezenie } from "../icons/index.js";
import { Button } from "../ui/Button.js";
import { Checkbox } from "../ui/Choice.js";
import { StanBledu, StanLadowania, StanPusty } from "../ui/stany.js";

/**
 * Wybor parametrow oceny (F-8: siatka checkboxow wprost na karcie, F-3).
 *
 * Wspolny dla formularza wgrywania, ekranu zatwierdzania podzialu i okna ponownej oceny. Sam nic
 * nie pobiera: dostaje stan listy z `useKatalogParametrow`, wybor i dwie funkcje zwrotne. Dzieki temu
 * kazdy ekran trzyma wybor po swojemu, a lista, jej stany i komunikaty wygladaja wszedzie tak samo.
 *
 * Cztery stany (P-6, S-5): ladowanie, blad z ponowieniem, pusta lista i dane. Zaznaczenie niczego
 * nie jest bledem tego komponentu - komunikat (np. "Zaznacz co najmniej jeden parametr") przychodzi
 * z zewnatrz w `blad` i staje przy grupie (F-12).
 *
 * Opis przy parametrze to zdanie "co oceniamy" z metodyki; pod grupa stoi zdanie o tym, ze kazdy
 * parametr to osobne, platne wywolanie modelu (P-4: klient wie, za co placi).
 */
export function WyborParametrow({
  katalog,
  wybrane,
  onZmiana,
  onPonow,
  blad,
  wylaczony = false,
  etykieta = "Parametry oceny",
  podpowiedz,
}: {
  katalog: StanKatalogu;
  /** Zaznaczone slugi. */
  wybrane: readonly string[];
  onZmiana: (wybrane: string[]) => void;
  /** Ponowienie pobrania listy po bledzie. */
  onPonow: () => void;
  /** F-12: komunikat przy grupie. */
  blad?: string;
  wylaczony?: boolean;
  etykieta?: string;
  /** Zastepuje domyslne zdanie pod grupa. */
  podpowiedz?: string;
}): React.JSX.Element {
  const baza = useId();
  const idEtykiety = `${baza}-etykieta`;
  const idPodpowiedzi = `${baza}-podpowiedz`;
  const idBledu = `${baza}-blad`;

  if (katalog.stan === "ladowanie") return <StanLadowania tekst="Wczytuję parametry oceny..." />;

  if (katalog.stan === "blad") {
    return (
      <StanBledu
        tytul="Nie udało się wczytać parametrów oceny"
        opis="Bez listy nie wiemy, co ocenić. Spróbuj ponownie za chwilę."
        szczegol={katalog.kod}
        akcja={
          <Button wariant="secondary" onClick={onPonow}>
            Spróbuj ponownie
          </Button>
        }
      />
    );
  }

  const parametry = katalog.katalog.soczewki;
  if (parametry.length === 0) {
    return (
      <StanPusty
        tytul="Nie ma włączonych parametrów oceny"
        opis="Metodyka na serwerze nie ma żadnego włączonego parametru, więc nie ma czego zaznaczyć."
      />
    );
  }

  const opisy = [idPodpowiedzi, blad ? idBledu : null].filter(Boolean).join(" ");

  return (
    <div className="field" role="group" aria-labelledby={idEtykiety} aria-describedby={opisy}>
      <span className="field-label" id={idEtykiety}>
        {etykieta}
      </span>
      <ul className="choice-list">
        {parametry.map((p) => (
          <li key={p.slug}>
            <Checkbox
              nazwa="lensSlugs"
              etykieta={p.title}
              opis={p.coOceniamy}
              zaznaczony={wybrane.includes(p.slug)}
              wylaczony={wylaczony}
              onZmiana={(zaznaczony) => onZmiana(przelaczParametr(wybrane, p.slug, zaznaczony, parametry))}
            />
          </li>
        ))}
      </ul>
      <span className="field-hint" id={idPodpowiedzi}>
        {podpowiedz ?? opisKosztuWyboru(wybrane.length)}
      </span>
      {blad && (
        <span className="field-error" id={idBledu} role="alert">
          <IconOstrzezenie size={14} />
          {blad}
        </span>
      )}
    </div>
  );
}
